import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  computed,
  input,
  OnInit,
  output,
  runInInjectionContext,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { debounce, groupBy } from '@app/utils/common';
import { enhanceWithKey } from '@app/utils/enhance-with-key';
import { ConnectedList } from '@app/utils/lists/connected-list/connected-list';
import { ConnectedListImpl } from '@app/utils/lists/connected-list/connected-list-impl';
import { MetaDataProperty } from '@app/utils/lists/meta-list';
import { loggerFor } from '@app/utils/logger';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { useResettingSignal } from '@app/utils/signals/use-resetting-signal';
import { pausableWatch, PausableWatch, watch } from '@app/utils/signals/watch';
import { StringKeys } from '@app/utils/string-keys';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-elements-selector',
  templateUrl: './elements-selector.component.html',
  styles: [],
  providers: [ngValueAccessor(ElementsSelectorComponent)],
  standalone: false,
  exportAs: 'elements-selector',
  host: {
    class: 'elements-selector',
    '[class.readonly]': 'readonly()',
    '[class.no-available]': 'noAvailableSection()',
  },
})
export class ElementsSelectorComponent<T> extends ReactiveComponent implements OnInit {
  calculateMetaData = input<(entry: T) => FilterableEntry>();
  elementTemplate = input<TemplateRef<any>>();
  elementsLabel = input<string>('general.choice');
  inputElements = input.required<T[]>({ alias: 'elements' });
  inputSelectedElements = input<T[]>(undefined, { alias: 'selectedElements' });
  noAvailableSection = input(false, { transform: coerceBooleanProperty });
  pageSize = input<number>(5);
  properties = input<MetaDataProperty[]>([]);
  readonly = input<boolean>(false);
  searchProperties = input<Array<StringKeys<T>>>();
  selectedElementsLabel = input<string>('general.chosen');
  showHeaderActions = input(false, { transform: coerceBooleanProperty });
  sortBy = input<(a: T, b: T) => number>();
  sortProperties = input<Array<StringKeys<T>>>();
  trackByProperty = input<keyof T>();

  valueChanged = output<T[]>();

  protected connectedList: ConnectedList<T> = new ConnectedListImpl();
  protected allItems = toSignal(this.connectedList.all$);
  protected value = toSignal(this.connectedList.value$);
  protected availablePage = computed(() => this.value().available);
  protected available = computed(() => this.availablePage().entries);
  protected highlightedElement = useResettingSignal<T | null>(null, 750);
  protected selectedPage = computed(() => this.value().selected);
  protected selected = computed(() => this.selectedPage().entries);

  protected updateFilter = debounce((term: string): void => {
    this.log.debug('updateFilter', term);

    this.connectedList.available.filter(term).draw();
    this.connectedList.selected.filter(term).draw();
  }, 400);

  private log = loggerFor('ElementsSelector');
  private watchInputs: PausableWatch;

  constructor() {
    super();

    this.destroyRef.onDestroy(() => this.connectedList.destroy());
  }

  ngOnInit(): void {
    runInInjectionContext(this.injector, () => {
      this.watchInputs = pausableWatch(
        [this.inputElements, this.inputSelectedElements],
        ([elements, selected]) => {
          this.log.debug('Elements changed', elements, selected);
          this.updatePages(elements, selected);
        }
      );

      watch([this.sortProperties, this.pageSize], ([sortProperties, pageSize]) => {
        this.log.debug('Sort and page size changed', sortProperties, pageSize);
        this.connectedList.sortBy(sortProperties).updatePageSize(pageSize).draw();
      });
    });
  }

  filterInputChanged(allElements: T[]): void {
    this.log.debug('Filter input changed', allElements);
    const { elements = [], selectedElements = [] } = groupBy(allElements, (item) =>
      this.selectedPage().allEntries.includes(item) ? 'selectedElements' : 'elements'
    );
    this.log.trace('Elements', elements, selectedElements);
    this.connectedList.updateFiltered(elements, selectedElements).draw();
  }

  removeAllVisible() {
    this.connectedList.unselectAllFiltered().draw();
    this.emit(this.selectedPage().allEntries);
  }

  selectAllVisible() {
    this.connectedList.selectAllFiltered().draw();
    this.emit(this.selectedPage().allEntries);
  }

  selectElement = (element: T) => {
    this.connectedList.select(element).draw();
    this.highlightedElement.set(element);
    this.emit(this.selectedPage().allEntries);
  };

  unselectElement = (element: T) => {
    this.connectedList.unselect(element).draw();
    this.highlightedElement.set(element);
    this.emit(this.selectedPage().allEntries);
  };

  updateAvailablePage($event: { page: number; pageSize: number }) {
    this.connectedList.available.goTo($event.page).draw();
  }

  updateSelectedPage($event: { page: number; pageSize: number }) {
    this.connectedList.selected.goTo($event.page).draw();
  }

  writeValue(value: T[] | null): void {
    this.log.debug('writeValue', value);

    if (value != null) {
      this.updatePages(this.inputElements(), value);
    }
  }

  private emit(elements: T[]): void {
    this.log.debug('Emitting elements', elements);
    this.watchInputs.ignoreNextUpdate();
    this.onChange(elements);
    this.valueChanged.emit(elements);
  }

  private updatePages(elements: T[], selectedElements: T[] | undefined): void {
    const { available, selected } = this.value();

    if (elements == null) {
      elements = available.allEntries;
    }

    if (selectedElements == null) {
      selectedElements = selected.allEntries;
    }

    if (this.searchProperties() != null) {
      elements = elements.map(enhanceWithKey(this.searchProperties()));
      selectedElements = selectedElements = selectedElements.map(enhanceWithKey(this.searchProperties()));
    }

    this.connectedList.update(elements, selectedElements).draw();
  }
}

function Debounced(delay: number): MethodDecorator {
  return (target) => {};
}
